import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { 
  Clock, 
  Star, 
  MapPin, 
  Navigation,
  Heart,
  ShoppingBag
} from 'lucide-react';
import { formatDistance } from './AddressAutocomplete';
import { checkIsOpen } from '../utils/openingHours';
import { getFullImageUrl, RESTAURANT_PLACEHOLDER } from '../utils/images';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function RestaurantCard({ restaurant, distance, isOpen: isOpenProp, animationDelay = 0 }) {
  // Use prop if provided, otherwise calculate from opening_hours
  const openStatus = isOpenProp !== undefined 
    ? { isOpen: isOpenProp, message: isOpenProp ? null : 'Fermé' }
    : checkIsOpen(restaurant.opening_hours);
  const isOpen = openStatus.isOpen;
  const { token, user } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loadingFavorite, setLoadingFavorite] = useState(false);

  // Determine the best image to display: featured_image_url > cover_image > first gallery photo > default
  const displayImage = restaurant.featured_image_url 
    || restaurant.cover_image 
    || (restaurant.gallery && restaurant.gallery.length > 0 ? restaurant.gallery[0] : null);
  
  // Handle relative URLs
  const imageUrl = getFullImageUrl(displayImage) || RESTAURANT_PLACEHOLDER;

  useEffect(() => {
    if (token && user) {
      checkFavorite();
    }
  }, [token, user, restaurant.id]);

  const checkFavorite = async () => {
    try {
      const response = await axios.get(`${API}/favorites/${restaurant.id}/check`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsFavorite(response.data.is_favorite);
    } catch {
      // Silently fail
    }
  };

  const toggleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (!token) {
      toast.error('Connectez-vous pour ajouter aux favoris');
      return;
    }

    setLoadingFavorite(true);
    try {
      if (isFavorite) {
        await axios.delete(`${API}/favorites/${restaurant.id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(false);
        toast.success('Retiré des favoris');
      } else {
        await axios.post(`${API}/favorites/${restaurant.id}`, {}, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsFavorite(true);
        toast.success('Ajouté aux favoris ❤️');
      }
    } catch {
      toast.error('Erreur lors de la mise à jour');
    } finally {
      setLoadingFavorite(false);
    }
  };

  return (
    <Link 
      to={`/restaurant/${restaurant.id}`}
      className={`group bg-white rounded-2xl overflow-hidden border border-gray-100 hover:shadow-lg transition-all animate-slide-up ${!isOpen ? 'opacity-75' : ''}`}
      style={{ animationDelay: `${animationDelay}s` }}
      data-testid={`restaurant-item-${restaurant.id}`}
    >
      <div className="aspect-[4/3] overflow-hidden relative">
        <img 
          src={imageUrl}
          alt={restaurant.name}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
          loading="lazy"
          decoding="async"
        />
        {restaurant.logo && (
          <div className="absolute bottom-3 left-3 w-12 h-12 rounded-xl bg-white shadow-md overflow-hidden">
            <img src={getFullImageUrl(restaurant.logo)} alt="" className="w-full h-full object-cover" loading="lazy" />
          </div>
        )}
        
        {/* Favorite Button */}
        <button
          onClick={toggleFavorite}
          disabled={loadingFavorite}
          className={`absolute top-3 right-3 w-10 h-10 rounded-full flex items-center justify-center transition-all ${
            isFavorite 
              ? 'bg-red-500 text-white shadow-lg' 
              : 'bg-white/90 text-gray-600 hover:bg-white hover:text-red-500 shadow-md'
          } ${loadingFavorite ? 'opacity-50' : ''}`}
        >
          <Heart className={`w-5 h-5 ${isFavorite ? 'fill-current' : ''}`} />
        </button>

        {/* Open/Closed Status Badge */}
        <div className="absolute top-3 left-3 flex flex-col gap-1.5">
          {isOpen ? (
            <Badge className="bg-emerald-500 text-white shadow-sm">
              <span className="w-1.5 h-1.5 bg-white rounded-full mr-1.5 animate-pulse"></span>
              Ouvert
              {openStatus.message && <span className="ml-1 opacity-80">• {openStatus.message}</span>}
            </Badge>
          ) : (
            <>
              <Badge className="bg-red-500 text-white shadow-sm">
                Fermé
                {openStatus.message && openStatus.message !== 'Fermé' && (
                  <span className="ml-1 opacity-80">• {openStatus.message}</span>
                )}
              </Badge>
              {restaurant.accepting_orders && (
                <Badge className="bg-emerald-600 text-white shadow-sm text-xs" data-testid="accepting-orders-badge">
                  <ShoppingBag className="w-3 h-3 mr-1" />
                  Commandes acceptées
                </Badge>
              )}
            </>
          )}
        </div>
      </div>
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <h3 className="font-semibold text-gray-900 group-hover:text-emerald-600 transition-colors line-clamp-1">
            {restaurant.name}
          </h3>
          {restaurant.avg_rating > 0 && (
            <span className="flex items-center gap-1 text-sm font-medium text-gray-700 bg-gray-100 px-2 py-0.5 rounded-full shrink-0">
              <Star className="w-3.5 h-3.5 fill-yellow-400 text-yellow-400" />
              {restaurant.avg_rating.toFixed(1)}
              {restaurant.total_reviews > 0 && (
                <span className="text-gray-400 text-xs">({restaurant.total_reviews})</span>
              )}
            </span>
          )}
        </div>
        <p className="text-sm text-gray-500 mt-1 line-clamp-1">
          {restaurant.cuisine_type?.join(', ') || 'Restaurant'}
        </p>
        <div className="flex items-center gap-3 mt-3 text-sm text-gray-500">
          <span className="flex items-center gap-1">
            <Clock className="w-4 h-4" />
            {restaurant.avg_prep_time} min
          </span>
          {distance !== null ? (
            <span className="flex items-center gap-1 text-emerald-600 font-medium">
              <Navigation className="w-4 h-4" />
              {formatDistance(distance)}
            </span>
          ) : (
            <span className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {restaurant.city}
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
